import { articleNotifications } from '~/server/notifications/article.notifications';
import { comicNotifications } from '~/server/notifications/comics.notifications';
import { articleRatingReviewNotifications } from '~/server/notifications/article-rating-review.notifications';
import { ratingReviewNotifications } from '~/server/notifications/rating-review.notifications';
import { articleUnpublishNotifications } from '~/server/notifications/article-unpublish.notifications';
import { creatorServiceNotifications } from '~/server/notifications/creator-service.notifications';
import { appBlockNotifications } from '~/server/notifications/app-block.notifications';
import { appCollaboratorNotifications } from '~/server/notifications/app-collaborator.notifications';
import { appFeedbackNotifications } from '~/server/notifications/app-feedback.notifications';
import { appListingNotifications } from '~/server/notifications/app-listing.notifications';
import { appModeratorMessageNotifications } from '~/server/notifications/app-moderator-message.notifications';
import { auctionNotifications } from '~/server/notifications/auction.notifications';
import type { BareNotification } from '~/server/notifications/base.notifications';
import { bountyNotifications } from '~/server/notifications/bounty.notifications';
import { buzzNotifications } from '~/server/notifications/buzz.notifications';
import { membershipGiftNotifications } from '~/server/notifications/membership-gift.notifications';
import { challengeNotifications } from '~/server/notifications/challenge.notifications';
import { collectionNotifications } from '~/server/notifications/collection.notifications';
import { commentNotifications } from '~/server/notifications/comment.notifications';
import { crucibleNotifications } from '~/server/notifications/crucible.notifications';
import { cosmeticShopNotifications } from '~/server/notifications/cosmetic-shop.notifications';
import { placementNotifications } from '~/server/notifications/placement.notifications';
import {
  storylineNotifications,
  storylineOwnerNotifications,
  storylineInviteNotifications,
} from '~/server/notifications/storyline.notifications';
import { creatorsProgramNotifications } from '~/server/notifications/creators-program.notifications';
import { featuredNotifications } from '~/server/notifications/featured.notifications';
import { creatorMilestoneNotifications } from '~/server/notifications/creator-milestone.notifications';
import { creatorAnnouncementNotifications } from '~/server/notifications/creator-announcement.notifications';
import { followNotifications } from '~/server/notifications/follow.notifications';
import { generationMuteNotifications } from '~/server/notifications/generation-mute.notifications';
import { reviewMuteNotifications } from '~/server/notifications/review-mute.notifications';
import { imageNotifications } from '~/server/notifications/image.notifications';
import { mentionNotifications } from '~/server/notifications/mention.notifications';
import { minorFlagNotifications } from '~/server/notifications/minor-flag.notifications';
import { textScanFlagNotifications } from '~/server/notifications/text-scan-flag.notifications';
import { modelNotifications } from '~/server/notifications/model.notifications';
import { knightsNewOrderNotifications } from '~/server/notifications/new-order.notifications';
import { strikeNotifications } from '~/server/notifications/strike.notifications';
import { reactionNotifications } from '~/server/notifications/reaction.notifications';
import { reportNotifications } from '~/server/notifications/report.notifications';
import { reviewNotifications } from '~/server/notifications/review.notifications';
import { systemNotifications } from '~/server/notifications/system.notifications';
import { unpublishNotifications } from '~/server/notifications/unpublish.notifications';
import { userJourneyNotifications } from '~/server/notifications/user-journey.notifications';
import { referralNotifications } from '~/server/notifications/referral.notifications';
import { textScanNotifications } from '~/server/notifications/text-scan.notifications';

export const notificationProcessors = {
  ...creatorAnnouncementNotifications,
  ...mentionNotifications,
  ...modelNotifications,
  ...reviewNotifications,
  ...commentNotifications,
  ...reactionNotifications,
  ...systemNotifications,
  ...userJourneyNotifications,
  ...unpublishNotifications,
  ...minorFlagNotifications,
  ...textScanFlagNotifications,
  ...articleNotifications,
  ...articleUnpublishNotifications,
  ...creatorServiceNotifications,
  ...appListingNotifications,
  ...appBlockNotifications,
  ...appCollaboratorNotifications,
  ...appFeedbackNotifications,
  ...appModeratorMessageNotifications,
  ...articleRatingReviewNotifications,
  ...ratingReviewNotifications,
  ...reportNotifications,
  ...featuredNotifications,
  ...bountyNotifications,
  ...placementNotifications,
  ...storylineNotifications,
  ...storylineOwnerNotifications,
  ...storylineInviteNotifications,
  ...buzzNotifications,
  ...collectionNotifications,
  ...imageNotifications,
  ...creatorsProgramNotifications,
  ...followNotifications,
  ...generationMuteNotifications,
  ...reviewMuteNotifications,
  ...cosmeticShopNotifications,
  ...challengeNotifications,
  ...crucibleNotifications,
  ...auctionNotifications,
  ...knightsNewOrderNotifications,
  ...comicNotifications,
  ...strikeNotifications,
  ...referralNotifications,
  ...membershipGiftNotifications,
  ...textScanNotifications,
  ...creatorMilestoneNotifications,
};

// Sort notifications by priority and group them by priority
const notifications = Object.entries(notificationProcessors)
  .map(([key, v]) => ({
    ...v,
    key,
  }))
  .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
const notificationPriorities: Record<number, typeof notifications> = {};
for (const notification of notifications) {
  const priority = notification.priority ?? 0;
  notificationPriorities[priority] ??= [];
  notificationPriorities[priority].push(notification);
}
// Numeric sort, not lexicographic: `send-notifications` runs these batches in order and the comment
// family relies on that order to decide which of several competing notifications wins the shared dedupe
// key. A default .sort() would put priority 10 ahead of priority 2.
export const notificationBatches = Object.keys(notificationPriorities)
  .map(Number)
  .sort((a, b) => a - b)
  .map((key) => notificationPriorities[key]);

export function getNotificationMessage(notification: Omit<BareNotification, 'id'>) {
  const { prepareMessage } = notificationProcessors[notification.type] ?? {};
  if (!prepareMessage) return null;
  return prepareMessage(notification);
}

function getNotificationTypes() {
  const notificationTypes: string[] = [];
  const optInNotificationTypes: string[] = [];
  const notificationCategoryTypes: Record<
    string,
    { displayName: string; type: string; optIn: boolean }[]
  > = {};
  for (const [type, { displayName, toggleable, category, optIn, showCategory }] of Object.entries(
    notificationProcessors
  )) {
    if (toggleable === false && !showCategory) continue;
    notificationCategoryTypes[category] ??= [];
    notificationCategoryTypes[category]!.push({ type, displayName, optIn: optIn ?? false });
    if (optIn) optInNotificationTypes.push(type);
    else notificationTypes.push(type);
  }

  return {
    notificationCategoryTypes,
    notificationTypes,
    optInNotificationTypes,
  };
}
/**
 * `notificationTypes` deliberately EXCLUDES opt-in types — it is the list the bulk on/off toggles
 * send, and for an opt-in type "off" writes the row that subscribes you. Those types are still
 * present in `notificationCategoryTypes` so they render their own checkbox.
 */
export const { notificationCategoryTypes, notificationTypes, optInNotificationTypes } =
  getNotificationTypes();

export const isOptInNotification = (type: string) => optInNotificationTypes.includes(type);

/**
 * Push may target exactly the types that render a control — registered and toggleable. Gates the
 * `updatePushSettings` insert so a direct API client can't create rows for unknown or
 * non-toggleable (system/moderation) types the UI never exposes.
 */
export const isPushableNotificationType = (type: string) => {
  const processor = notificationProcessors[type];
  return !!processor && processor.toggleable !== false;
};

export const emailNotificationTypeList = Object.entries(notificationProcessors)
  .filter(([, processor]) => processor.defaultEmail)
  .map(([type]) => type);

export const isEmailNotificationType = (type: string) => emailNotificationTypeList.includes(type);
